/**
 * Shared API-error normaliser
 * ---------------------------
 * Converts arbitrary backend / axios / network error payloads into a
 * SAFE user-facing STRING that can be rendered directly in JSX,
 * fed into `toast.error(...)`, or displayed inside a form-error banner
 * without ever risking a "React error #31: object with keys …" crash.
 *
 * Why this exists
 * ---------------
 * FastAPI's default 422 response body is a *list of validation errors*:
 *
 *     { "detail": [
 *         { "type": "value_error",
 *           "loc": ["body", "email"],
 *           "msg":  "value is not a valid email address",
 *           "input": "not-an-email",
 *           "ctx":  { "reason": "..." } }
 *     ] }
 *
 * Passing that `detail` array straight into React as `{error}` (which
 * ~40 legacy call-sites in this codebase used to do) makes React try
 * to render each object as a child → minified error #31.  On mobile /
 * Safari builds this bubbled up into the app-wide `<AppErrorBoundary>`,
 * showing users a scary "Something went wrong" splash after every
 * validation failure — including the registration screen.
 *
 * Guarantees
 * ----------
 *   • Return type is ALWAYS a non-empty string.
 *   • `input`, `ctx`, stack traces, backend object graphs, and raw
 *     JSON are NEVER surfaced.
 *   • Nested arrays / objects are recursively flattened but capped
 *     at 4 messages to keep toasts readable.
 *
 * Written in CommonJS so the Jest suite in this project (which does
 * NOT have babel-jest wired up) can `require()` it directly, while
 * the React source still consumes it via ES `import` because
 * webpack / CRA transparently converts `module.exports`.
 */

const FIELD_LABELS = {
  email: 'Email',
  password: 'Password',
  new_password: 'Password',
  current_password: 'Current password',
  confirm_password: 'Password confirmation',
  name: 'Name',
  full_name: 'Full name',
  first_name: 'First name',
  last_name: 'Last name',
  phone: 'Mobile number',
  phone_number: 'Mobile number',
  role: 'Account type',
  date_of_birth: 'Date of birth',
  dob: 'Date of birth',
  referral_code: 'Referral code',
  plan_id: 'Plan',
  tier: 'Plan',
  billing_period: 'Billing period',
  code: 'Code',
  token: 'Reset link',
};

const humaniseFieldName = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  if (FIELD_LABELS[s.toLowerCase()]) return FIELD_LABELS[s.toLowerCase()];
  return s
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
};

const fieldFromLoc = (loc) => {
  if (!Array.isArray(loc) || loc.length === 0) return '';
  const parts = loc.filter(
    (p) => p !== 'body' && p !== 'query' && p !== 'path' && p !== 'header',
  );
  const last = parts.length ? parts[parts.length - 1] : loc[loc.length - 1];
  return humaniseFieldName(last);
};

const humaniseMsg = (msg, type) => {
  if (!msg && !type) return '';
  const raw = String(msg || '').trim();
  const lower = raw.toLowerCase();
  const t = String(type || '').toLowerCase();
  if (lower === 'field required' || lower === 'missing' || t === 'missing') return 'is required';
  if (lower.startsWith('value is not a valid email address')) return 'is not a valid email address';
  if (t === 'string_too_short' || lower.startsWith('string should have at least')) return 'is too short';
  if (t === 'string_too_long' || lower.startsWith('string should have at most')) return 'is too long';
  return raw.replace(/^value error,\s*/i, '');
};

const formatPydanticEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return '';
  const field = fieldFromLoc(entry.loc);
  const msg = humaniseMsg(entry.msg, entry.type);
  if (field && msg) return `${field} ${msg}`.replace(/\s+/g, ' ').trim();
  if (msg) return msg;
  if (field) return `${field} is invalid`;
  return '';
};

const normaliseApiErrorDetail = (
  detail,
  fallback = 'Something went wrong. Please try again.',
) => {
  if (detail == null) return fallback;
  if (typeof detail === 'string') {
    const s = detail.trim();
    return s || fallback;
  }
  if (typeof detail === 'number' || typeof detail === 'boolean') {
    return String(detail);
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((e) => {
        if (Array.isArray(e)) return normaliseApiErrorDetail(e, '');
        if (e && typeof e === 'object') {
          const pretty = formatPydanticEntry(e);
          if (pretty) return pretty;
          if (typeof e.msg === 'string') return e.msg;
          if (typeof e.message === 'string') return e.message;
          return '';
        }
        if (typeof e === 'string') return e.trim();
        return '';
      })
      .filter(Boolean)
      .slice(0, 4);
    if (messages.length === 0) {
      return 'Please check the highlighted fields and try again.';
    }
    return messages.join(' · ');
  }
  if (typeof detail === 'object') {
    const pretty = formatPydanticEntry(detail);
    if (pretty) return pretty;
    if (typeof detail.msg === 'string') return detail.msg;
    if (typeof detail.message === 'string') return detail.message;
    if (typeof detail.error === 'string') return detail.error;
    if ('detail' in detail) return normaliseApiErrorDetail(detail.detail, fallback);
    return fallback;
  }
  return fallback;
};

const normaliseApiError = (
  error,
  fallback = 'Something went wrong. Please try again.',
) => {
  if (error == null) return fallback;
  if (typeof error === 'string') return error.trim() || fallback;

  const response = error.response;
  const data = response && response.data;

  if (data && data._shielded) {
    return 'Connection blip — please retry in a moment.';
  }

  if (data && typeof data === 'object') {
    if ('detail' in data) return normaliseApiErrorDetail(data.detail, fallback);
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;
  }
  if (typeof data === 'string' && data.trim()) return data.trim();

  if (!response) {
    const msg = String(error.message || '').toLowerCase();
    if (msg.includes('network') || msg.includes('failed to fetch')) {
      return 'Network issue — please check your connection and try again.';
    }
    if (msg.includes('timeout') || msg.includes('econnaborted')) {
      return 'The server took too long to respond. Please try again.';
    }
    if (msg.includes('canceled') || msg.includes('cancelled')) {
      return fallback;
    }
    return error.message ? String(error.message) : fallback;
  }

  const status = response.status;
  if (status === 401) return 'Please sign in and try again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'That resource could not be found.';
  if (status === 409) return 'That change conflicts with the current state. Please refresh and try again.';
  if (status === 429) return 'Too many requests — please slow down and try again shortly.';
  if (status >= 500) return 'Our servers are having trouble right now. Please try again shortly.';

  return fallback;
};

module.exports = {
  normaliseApiError,
  normaliseApiErrorDetail,
  // Extra internal helpers exposed for unit tests.
  __internals: { humaniseFieldName, humaniseMsg, formatPydanticEntry, fieldFromLoc },
};
module.exports.default = normaliseApiError;
